import { useEffect, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMailsEnvoi, useRelancesReglement } from '../api/hooks';
import { LIBELLE_CANAL, type CanalEnvoi, type CommandeListee, type MailEnvoiLu } from '../api/types';
import { dateHeure } from '../format';
import { Chargement, Erreur } from './Etats';
import { Repousser } from './Repousser';

/**
 * Le mail envoyé pour un envoi, relu dans Outlook.
 *
 * Répond à « qu'est-ce qui est parti ? » sans quitter la fiche : le message
 * envoyé depuis contact@ — ou, pour un mail d'avant l'envoi direct
 * (2026-10-02), le brouillon s'il attend encore dans Outlook. Le corps
 * s'affiche dans un cadre isolé, sans script : c'est du HTML venu de la boîte,
 * pas du code de l'application.
 *
 * Une commande antérieure à l'enregistrement des identifiants n'a rien à
 * relire : la fenêtre le dit, et propose de refaire le mail si c'est utile.
 */
export function MailDepose({
  commande, canal, onFermer, onRefaire, libelleRefaire,
}: {
  commande: CommandeListee;
  canal: CanalEnvoi;
  onFermer: () => void;
  /** Rouvre la préparation du mail, pour le renvoyer. Absent : pas de bouton. */
  onRefaire?: () => void;
  libelleRefaire?: string;
}) {
  // Rien d'enregistré : inutile d'interroger Outlook.
  const lecture = useMailsEnvoi(commande.id, canal, commande.envois[canal].mails > 0);
  return (
    <FenetreMails
      titre={`${LIBELLE_CANAL[canal]} — ${commande.ref}`}
      lecture={commande.envois[canal].mails === 0 ? null : lecture}
      vide={
        <>
          Ce mail a été envoyé avant que l&apos;application n&apos;enregistre les mails
          qu&apos;elle envoie : il n&apos;y a rien à relire ici. Il se retrouve dans Outlook en
          cherchant la référence {commande.ref}.
        </>
      }
      onFermer={onFermer} onRefaire={onRefaire} libelleRefaire={libelleRefaire}
      pied={<Repousser commande={commande} />}
    />
  );
}

/** Les relances de règlement envoyées au client, de la plus récente à la plus ancienne. */
export function RelancesReglementDeposees({
  commande, onFermer,
}: {
  commande: CommandeListee;
  onFermer: () => void;
}) {
  const lecture = useRelancesReglement(commande.id, commande.relanceReglement.nombre > 0);
  return (
    <FenetreMails
      titre={`Relances de règlement — ${commande.ref}`}
      lecture={commande.relanceReglement.nombre === 0 ? null : lecture}
      ordre="recent"
      vide="Aucune relance de règlement n’a été envoyée pour cette commande."
      onFermer={onFermer}
    />
  );
}

/**
 * La fenêtre elle-même, indépendante de ce qu'elle relit.
 *
 * `lecture` à `null` : rien n'a été enregistré, inutile d'interroger Outlook —
 * `vide` dit pourquoi.
 */
function FenetreMails({
  titre, lecture, vide, ordre = 'depot', onFermer, onRefaire, libelleRefaire, pied,
}: {
  titre: string;
  lecture: UseQueryResult<MailEnvoiLu[]> | null;
  vide: ReactNode;
  /** `recent` : la dernière en tête, pour un historique de relances. */
  ordre?: 'depot' | 'recent';
  onFermer: () => void;
  onRefaire?: () => void;
  libelleRefaire?: string;
  /** Calé à gauche du pied — « Repousser », quand la fenêtre relit un mail de la commande. */
  pied?: ReactNode;
}) {
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const mails = lecture?.data ?? [];
  const affiches = ordre === 'recent' ? [...mails].reverse() : mails;

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={titre} style={{ width: 760 }}>
        <div className="mhead">
          <h2>{titre}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          {!lecture ? (
            <p className="hint" style={{ margin: 0 }}>{vide}</p>
          ) : lecture.isLoading ? (
            <Chargement />
          ) : lecture.error ? (
            <Erreur erreur={lecture.error} onReessayer={() => void lecture.refetch()} />
          ) : (
            affiches.map((m, i) => <Mail key={m.messageId ?? i} mail={m} />)
          )}
        </div>

        <div className="mfoot">
          {pied}
          {onRefaire && (
            <button type="button" className="btn" onClick={onRefaire}>
              {libelleRefaire ?? 'Refaire le mail'}
            </button>
          )}
          <button type="button" className="btn pri" onClick={onFermer}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Mail({ mail }: { mail: MailEnvoiLu }) {
  const m = mail.message;
  const lien = m?.lienWeb ?? mail.lienWeb;

  return (
    <section className="mail-depose">
      <dl className="kv" style={{ marginBottom: 10 }}>
        {mail.societe && <><dt>Fournisseur</dt><dd style={{ textAlign: 'left' }}>{mail.societe}</dd></>}
        <dt>À</dt>
        <dd style={{ textAlign: 'left' }}>{(m?.destinataires ?? mail.destinataires).join(', ') || '—'}</dd>
        {m && m.copie.length > 0 && <><dt>Cc</dt><dd style={{ textAlign: 'left' }}>{m.copie.join(', ')}</dd></>}
        <dt>Objet</dt><dd style={{ textAlign: 'left' }}>{m?.objet ?? mail.objet ?? '—'}</dd>
        <dt>Statut</dt>
        <dd style={{ textAlign: 'left' }}>
          {!m ? (
            mail.deposeLe ? `Envoyé le ${dateHeure(mail.deposeLe)}` : 'Envoyé'
          ) : m.brouillon ? (
            <span style={{ color: 'var(--brick)' }}>
              Resté en brouillon dans Outlook — pas envoyé
            </span>
          ) : (
            <span style={{ color: 'var(--pine)' }}>
              Envoyé{m.envoyeLe ? ` le ${dateHeure(m.envoyeLe)}` : ''}
            </span>
          )}
        </dd>
      </dl>

      {m ? (
        /* `sandbox` vide : ni script, ni formulaire, ni accès à l'application.
           Le HTML vient de la boîte Outlook, il s'affiche et c'est tout. */
        <iframe
          title={`Contenu du mail ${m.objet}`}
          sandbox=""
          srcDoc={m.corpsHtml}
          className="mail-corps"
        />
      ) : (
        <p className="hint" style={{ margin: 0 }}>{mail.raison ?? 'Le mail n’a pas pu être relu.'}</p>
      )}

      {lien && (
        <p style={{ margin: '8px 0 0', fontSize: 12.5 }}>
          <a href={lien} target="_blank" rel="noreferrer">Ouvrir dans Outlook</a>
        </p>
      )}
    </section>
  );
}
